import {
  Calendar,
  CheckCircle,
  CheckCircle2,
  Clock,
  Edit,
  Plus,
  ScanEyeIcon,
} from "lucide-react";
import React, { useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import MainButton from "../../../../../components/MainButton";
import { useLanguage } from "../../../../../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import DrawerEditContent from "../Content/DrawerEditContent";
import DialogDeleteQuiz from "./DialogDeleteQuiz";
import { DateFormate } from "../../../../../lib/utils";
import { Tooltip } from "antd";
import { SubLessonData } from "../../../../../lib/languages";
import { useAuth } from "../../../../../context/AuthContext";
import QuizCard from "../../../../User/Quiz/_components/quiz-card";

const formatDuration = (duration) => {
  const [h = 0, m = 0] = duration.split(":").map(Number);
  if (h > 0 && m > 0) return `${h} hr ${m} min`;
  if (h > 0) return `${h} hr`;
  if (m > 0) return `${m} min`;
  return duration;
};

export default function QuizTab({ quizzes, fetchQuizzes }) {
  const { user } = useAuth();
  const isTeacher = user?.role === "teacher";

  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div className="grid">
      {quizzes && quizzes.length > 0 ? (
        <div>
          {!isTeacher ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quizzes.map((quiz, i) => (
                <QuizCard
                  key={i}
                  quiz={quiz}
                  onStart={() => navigate("/quiz-exam", { state: { quiz } })}
                />
              ))}
            </div>
          ) : (
            <div className="grid gap-4">
              {quizzes.map((quiz) => (
                <div
                  key={quiz.id}
                  className="flex items-center justify-between flex-wrap py-4 border-b relative"
                  onMouseEnter={() => setHoveredCard(quiz.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Quiz Details */}
                  <div className="flex items-center gap-2">
                    {LightSvg}
                    <span className="font-medium truncate">
                      {quiz.title[language] ||
                        quiz.title.en ||
                        quiz.title.ar ||
                        t.quizUntitled}
                    </span>
                    {" ◾ "}
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {formatDuration(quiz.duration)}
                    </div>
                    {" ◾ "}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          <Tooltip title="Start Date">
                            {DateFormate(quiz.start_date)}
                          </Tooltip>
                        </span>
                      </div>
                      {" ◾ "}
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          <Tooltip title="End Date">
                            {DateFormate(quiz.end_date)}
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Show & Edit & Delete */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary"
                      onClick={() =>
                        navigate(`/teacher/show-quiz-answers/${quiz.id}`)
                      }
                    >
                      {t.quizShowAnswers}
                      <ScanEyeIcon className="h-4 w-4 ml-1 text-blue-600" />
                    </Button>

                    <AnimatePresence>
                      {hoveredCard === quiz.id && (
                        <>
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-500 h-7 w-7"
                              onClick={() =>
                                navigate(`/teacher/edit-quiz/${quiz.bid}`, {
                                  state: { lessonId: id },
                                })
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2, delay: 0.05 }}
                          >
                            <DialogDeleteQuiz
                              item={quiz}
                              fetchQuizzes={fetchQuizzes}
                            />
                          </motion.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500">{t.quizNoQuizzes}</div>
      )}

      {isTeacher && (
        <div className="text-end mt-4">
          <MainButton
            Icon={Plus}
            title={t.addQuiz}
            onClick={() => navigate(`/teacher/add-quiz/${id}`)}
          />
        </div>
      )}
    </div>
  );
}

const LightSvg = (
  <svg
    width="30"
    height="31"
    viewBox="0 0 30 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24.5275 8.46283C23.719 3.83979 19.7112 0.5 15.0002 0.5C10.2893 0.5 6.28143 3.83979 5.47296 8.46283C4.92803 11.6621 5.98266 14.8965 8.30303 17.1288C9.73788 18.5031 10.6057 20.4516 10.6057 22.4727V23.3516L12.3635 25.168H15.0002H17.637L19.3948 23.3516V22.4727C19.3948 20.4539 20.261 18.5046 21.6975 17.1288C24.0178 14.8965 25.0724 11.6621 24.5275 8.46283Z"
      fill="#2563EB"
    />
    <path
      d="M19.3945 23.3516V22.4727C19.3945 20.4539 20.2608 18.5046 21.6973 17.1288C24.0176 14.8965 25.0722 11.6621 24.5273 8.46283C23.7188 3.83979 19.7109 0.5 15 0.5V25.168H17.6367L19.3945 23.3516Z"
      fill="#204AAB"
    />
    <path
      d="M0.925781 4.36719L2.16843 3.12454L4.75584 5.71195L3.51319 6.9546L0.925781 4.36719Z"
      fill="#2563EB"
    />
    <path d="M0 9.28906H3.57422V11.0469H0V9.28906Z" fill="#2563EB" />
    <path
      d="M0.945312 15.9463L3.53016 13.3614L4.77281 14.6041L2.18796 17.1889L0.945312 15.9463Z"
      fill="#2563EB"
    />
    <path
      d="M15 23.3516H10.6055V26.1055C10.6055 28.5312 12.5742 30.5 15 30.5C17.4258 30.5 19.3945 28.5312 19.3945 26.1055V23.3516H15Z"
      fill="#EDF5FF"
    />
    <path
      d="M19.3945 26.1055V23.3516H15V30.5C17.4258 30.5 19.3945 28.5312 19.3945 26.1055Z"
      fill="#D5E8FE"
    />
    <path
      d="M25.2402 5.71387L27.8276 3.1265L29.0704 4.36927L26.483 6.95664L25.2402 5.71387Z"
      fill="#204AAB"
    />
    <path d="M26.4258 9.28906H30V11.0469H26.4258V9.28906Z" fill="#204AAB" />
    <path
      d="M25.2207 14.6074L26.4635 13.3646L29.049 15.9502L27.8063 17.193L25.2207 14.6074Z"
      fill="#204AAB"
    />
    <path
      d="M17.6367 8.41016C17.6367 9.24512 17.2348 10.0391 16.5621 10.5342C16.1344 10.8494 15.8789 11.3539 15.8789 11.8842V12.8047H14.1211V11.8842C14.1211 11.0346 14.441 10.2166 15 9.59199C15.1553 9.41855 15.3293 9.25977 15.5203 9.11914C15.7482 8.95098 15.8789 8.69258 15.8789 8.41016C15.8789 7.92559 15.4846 7.53125 15 7.53125C14.5154 7.53125 14.1211 7.92559 14.1211 8.41016V9.28906H12.3633V8.41016C12.3633 6.95645 13.5463 5.77344 15 5.77344C16.4537 5.77344 17.6367 6.95645 17.6367 8.41016Z"
      fill="white"
    />
    <path d="M14.1211 14.5625H15.8789V16.3203H14.1211V14.5625Z" fill="white" />
    <path d="M15 14.5625H15.8789V16.3203H15V14.5625Z" fill="#9A9DA5" />
    <path
      d="M17.6367 8.41016C17.6367 9.24512 17.2348 10.0391 16.5621 10.5342C16.1344 10.8494 15.8789 11.3539 15.8789 11.8842V12.8047H15V9.59199C15.1553 9.41855 15.3293 9.25977 15.5203 9.11914C15.7482 8.95098 15.8789 8.69258 15.8789 8.41016C15.8789 7.92559 15.4846 7.53125 15 7.53125V5.77344C16.4537 5.77344 17.6367 6.95645 17.6367 8.41016Z"
      fill="#9A9DA5"
    />
  </svg>
);
